/**
 * Where toasts live, which turned out to be a layering question rather than a
 * styling one.
 *
 * They used to render inside `<main>` at `z-30`, under a stack that goes:
 * sheet overlay 40, sheet content 50, tour 55, stage 56. Every delete in this
 * app happens inside a sheet and commits immediately, offering the only way
 * back as an undo button on a toast — so the one toast in the app that guards
 * data was also the one most likely to be painted underneath the thing that
 * raised it. The host is now a portal on the body, above all of that.
 *
 * That fixes sheets and leaves one case it must not fix: her performances. A
 * toast over the Sunday report would be the app talking over itself. Rather
 * than solve that with a z-index — which would put the undo offer back
 * underneath something — the stage closes a gate in the store, and everything
 * except errors and undo offers waits behind it. See `closeGate`.
 *
 * The single `aria-live` region is mounted here unconditionally and never
 * unmounts. A live region that comes and goes is one that screen readers stop
 * watching, so it stays put and empty rather than being conditionally rendered.
 */

import { createPortal } from 'react-dom'
import { ToastCard } from './ToastCard.js'
import { useToasts } from './toastStore.js'

export function Toasts() {
  const visible = useToasts((state) => state.visible)
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      // `polite` for the room in general; anything that failed carries its own
      // assertive region below, because a failure announced behind three
      // queued confirmations is a failure nobody heard.
      aria-live="polite"
      aria-relevant="additions"
      // Capped and centred rather than edge-to-edge: on a phone the cap never
      // binds and this is the full width minus the gutters, but a sentence
      // stretched across a tablet is a banner, not something that arrived.
      className="pointer-events-none fixed inset-x-3 bottom-[calc(var(--tabbar-h,4.25rem)+0.5rem)] z-[60] mx-auto flex max-w-[26rem] flex-col gap-2"
    >
      {visible.map((toast) =>
        toast.srOnly === true ? (
          <span key={toast.id} className="sr-only">
            {toast.message}
          </span>
        ) : (
          <ToastCard key={toast.id} item={toast} />
        ),
      )}
    </div>,
    document.body,
  )
}
